import { useSnackbar } from "notistack";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

interface Args {
  /** The user asked for an export. The caller owns this state: it also widens the geocoding. */
  requested: boolean;
  setRequested: (requested: boolean) => void;
  /** Trips are still coming in; the set of rows to export is not final. */
  loading: boolean;
  /** Addresses of everything `requested` asks for have resolved. */
  addressesReady: boolean;
  addressesFailed: boolean;
  /** Builds and downloads the file; called with the render in which the addresses are ready. */
  run: () => void | Promise<void>;
}

/**
 * Defers an export until the addresses of all exported rows are in. The caller widens the
 * geocoding to every row while `requested` is set; once the addresses are ready `run` is called
 * exactly once and `requested` is cleared. A failed lookup cancels the export with a message
 * instead of producing a file with empty addresses.
 */
export function useExportWhenAddressesReady({
  requested,
  setRequested,
  loading,
  addressesReady,
  addressesFailed,
  run,
}: Args) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!requested || loading) return;
    if (addressesFailed) {
      setRequested(false);
      enqueueSnackbar(t("logbookAudit.export.addressesFailed"), { variant: "error" });
    } else if (addressesReady) {
      setRequested(false);
      void run();
    }
  }, [requested, setRequested, loading, addressesFailed, addressesReady, run, enqueueSnackbar, t]);
}
